import React, { useCallback, useEffect, useState } from "react";
import {
  Plus,
  Loader2,
  CalendarDays,
  Clock,
  Users,
  MapPin,
  Pencil,
  Trash2,
  AlertTriangle,
} from "lucide-react";
import axiosInstance from "../services/axiosinstance";
import { apiUrl } from "../routes/apiRoutes";
import AddBatchForm from "../Components/AddBatchForm";
import { useSearchParams } from "react-router";

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

const BatchManagementPage = () => {
  const [searchparams] = useSearchParams();
  const [showForm, setShowForm] = useState(false);
  const [editingBatch, setEditingBatch] = useState(null);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [softDeletingId, setSoftDeletingId] = useState(null);
  const [hardDeletingId, setHardDeletingId] = useState(null);
  const controller = new AbortController();

  const fetchBatches = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get(apiUrl.GET_BATCHES, {signal: controller.signal});
      if (res.data?.status && Array.isArray(res.data?.data)) {
        let list = res.data.data;

        if (searchparams.has("instructorId")) {
          const instructorId = searchparams.get("instructorId");
          list = list.filter((batch) => batch.instructorId === instructorId);
        }
        if (searchparams.has("batchstatus")) {
          const status = (searchparams.get("batchstatus") || "").toLowerCase();
          list = list.filter(
            (batch) => (batch.status || "").toLowerCase() === status,
          );
        }
        if (searchparams.has("courseId")) {
          const courseId = searchparams.get("courseId");
          list = list.filter((batch) => batch.courseId === courseId);
        }

        setBatches(list);
      } else {
        setBatches([]);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load batches");
      setBatches([]);
    } finally {
      setLoading(false);
    }
  }, [searchparams]);

  useEffect(() => {
    fetchBatches();
    return () => {
      controller.abort();
    };
  }, [fetchBatches]);

  const handleSave = () => {
    setShowForm(false);
    setEditingBatch(null);
    fetchBatches();
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingBatch(null);
  };

  const handleEdit = (batch) => {
    setEditingBatch(batch);
    setShowForm(true);
  };

  const handleSoftDelete = async (batch) => {
    if (!window.confirm(`Soft delete batch "${batch.batchName}"?`)) return;
    setSoftDeletingId(batch._id);
    try {
      await axiosInstance.delete(
        `${apiUrl.SOFT_DELETE_BATCH}/${batch._id}/soft`,
      );
      await fetchBatches();
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to soft delete batch";
      alert(msg);
    } finally {
      setSoftDeletingId(null);
    }
  };

  const handleHardDelete = async (batch) => {
    if (
      !window.confirm(
        `Permanently delete "${batch.batchName}"? This action cannot be undone.`,
      )
    )
      return;
    setHardDeletingId(batch._id);
    try {
      await axiosInstance.delete(`${apiUrl.HARD_DELETE_BATCH}/${batch._id}`);
      await fetchBatches();
    } catch (err) {
      const msg =
        err.response?.data?.message || "Failed to permanently delete batch";
      alert(msg);
    } finally {
      setHardDeletingId(null);
    }
  };

  if (showForm) {
    return (
      <div className="space-y-6">
        <AddBatchForm
          initialData={editingBatch ?? undefined}
          onSuccess={handleSave}
          onCancel={handleCancel}
        />
      </div>
    );
  }

  function batchFilterStatus() {
    if (searchparams.size === 0) return "All batches";
    if (searchparams.has("batchstatus")) {
      const status = searchparams.get("batchstatus") || "";
      return status.charAt(0).toUpperCase() + status.slice(1) + " batches";
    }
    if (searchparams.has("instructorName")) {
      return `Batches by ${searchparams.get("instructorName")}`;
    }
    if (searchparams.has("instructorId")) {
      return "Batches by instructor";
    }
    if (searchparams.has("courseId")) {
      return "Batches for course";
    }
    return "Filtered batches";
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-200">Batches</h2>
        <button
          type="button"
          onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-700"
        >
          <Plus size={16} />
          Add batch
        </button>
      </div>

      <section>
        <h3 className="mb-3 text-xs font-medium  first-letter:uppercase tracking-wide text-slate-500">
          {batchFilterStatus()}
        </h3>
        {loading ? (
          <div className="flex items-center justify-center rounded-lg border border-[#1d3350] bg-[#0f1c2e] py-12">
            <Loader2 size={28} className="animate-spin text-slate-400" />
          </div>
        ) : error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-4 text-center text-sm text-red-700">
            {error}
          </div>
        ) : batches.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-[#1d3350] bg-[#0f1c2e] py-12 text-center">
            <CalendarDays size={36} className="text-slate-300" />
            <p className="mt-2 text-sm text-slate-500">No batches yet</p>
            <button
              type="button"
              onClick={() => setShowForm(true)}
              className="mt-2 text-sm font-medium text-slate-200 underline hover:no-underline"
            >
              Add your first batch
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {batches.map((batch) => {
              const isSoftDeleted = batch.isDeleted;
              return (
                <article
                  key={batch._id}
                  className={`flex flex-col overflow-hidden rounded-lg border bg-[#0f1c2e] shadow-sm transition-shadow hover:shadow ${
                    isSoftDeleted
                      ? "border-amber-300 bg-amber-50/60"
                      : "border-[#1d3350]"
                  }`}
                >
                  <div className="border-b border-[#17283f] bg-slate-900/90 px-3 py-2 text-xs font-semibold  first-letter:uppercase tracking-wide text-slate-100">
                    {batch.batchId || "Batch"}
                  </div>
                  <div className="flex min-h-0 flex-1 flex-col p-3">
                    <div className="mb-1 flex items-center justify-between gap-2">
                      <h4 className="truncate text-sm font-semibold text-slate-100">
                        {batch.batchName}
                      </h4>
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold  first-letter:uppercase ${
                          batch.status === "ongoing"
                            ? "bg-emerald-100 text-emerald-800"
                            : batch.status === "upcoming"
                              ? "bg-sky-100 text-sky-800"
                              : batch.status === "completed"
                                ? "bg-[#13233a] text-slate-300"
                                : "bg-amber-100 text-amber-800"
                        }`}
                      >
                        {batch.status || "—"}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">
                      {batch.courseName || "Unknown course"}
                    </p>
                    <div className="mt-2 space-y-1.5 text-xs text-slate-400">
                      <div className="flex items-center gap-1.5">
                        <CalendarDays size={12} />
                        <span>
                          {formatDate(batch.startDate)} –{" "}
                          {formatDate(batch.endDate)}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Clock size={12} />
                        <span>{batch.timing}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <MapPin size={12} />
                        <span className="capitalize">
                          {batch.mode || "offline"} · {batch.days}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Users size={12} />
                        <span>{batch.seatsLeft} seats left</span>
                      </div>
                      {isSoftDeleted && (
                        <div className="mt-1 inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
                          <AlertTriangle size={10} />
                          <span>Soft deleted</span>
                        </div>
                      )}
                    </div>

                    <div className="mt-3 flex items-center gap-1 border-t border-[#17283f] pt-2">
                      <button
                        type="button"
                        onClick={() => handleEdit(batch)}
                        className="flex h-8 flex-1 items-center justify-center gap-1 rounded bg-[#13233a] text-xs font-medium text-slate-300 hover:bg-slate-200"
                      >
                        <Pencil size={12} />
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSoftDelete(batch)}
                        disabled={softDeletingId === batch._id}
                        className="flex h-8 w-9 items-center justify-center rounded bg-amber-50 text-amber-700 hover:bg-amber-100 disabled:opacity-60"
                        title="Soft delete"
                      >
                        {softDeletingId === batch._id ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <Trash2 size={14} />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleHardDelete(batch)}
                        disabled={hardDeletingId === batch._id}
                        className="flex h-8 w-9 items-center justify-center rounded bg-red-50 text-red-600 hover:bg-red-100 disabled:opacity-60"
                        title="Hard delete (permanent)"
                      >
                        {hardDeletingId === batch._id ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <AlertTriangle size={14} />
                        )}
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default BatchManagementPage;
